@file:Suppress("UnsafeCastFromDynamic")
package chat

import kotlin.js.Promise

private val window: dynamic = js("window")
private val document: dynamic = js("document")
private const val STORAGE_KEY = "nouveau-chat.history.v1"
private const val SELECTED_KEY = "nouveau-chat.selected.v1"
private const val CAT_ICON = """<svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M7 14V5l7 5h4l7-5v9c4 10-1 14-9 14S3 24 7 14Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><circle cx="12" cy="17" r="1" fill="currentColor"/><circle cx="20" cy="17" r="1" fill="currentColor"/><path d="m14 22 2 2 2-2M3 20l6 1m14 0 6-1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>"""
private const val PLUS_ICON = """<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>"""
private const val ARROW_ICON = """<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 19V5m-6 6 6-6 6 6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>"""
private var persistenceWarning = ""
private var busy = false
private var history = CatHistory()
private var sidebarOpen = false
private var viewToken = 0
private fun element(id: String): dynamic = document.getElementById(id)

fun main() {
    history = restore()
    element("app").innerHTML = """
        <aside class="sidebar" id="sidebar" aria-label="Historique des chats">
          <div class="brand">$CAT_ICON<span>Nouveau chat</span><button id="close-menu" class="close-menu" aria-label="Fermer l’historique">×</button></div>
          <button class="new-button create" id="sidebar-new">$PLUS_ICON Nouveau chat</button>
          <p class="history-label">VOS CHATS</p><nav class="history" id="history" aria-label="Anciens chats"></nav>
          <p class="local-note"><strong>Vos chats restent ici.</strong><br>Historique dans ce navigateur, jusqu’à 100 chats. Les photos dépendent de Cataas.</p>
        </aside>
        <button class="overlay" id="overlay" tabindex="-1" aria-label="Fermer l’historique"></button>
        <main class="main">
          <header class="topbar"><button class="menu" id="menu" aria-label="Ouvrir l’historique" aria-expanded="false" aria-controls="sidebar">☰</button><span class="title">Nouveau chat</span><span class="pill">100% FÉLIN</span><span class="top-hint">Le chat, au sens propre.</span></header>
          <section class="stage" id="stage" aria-label="Votre chat"></section>
          <footer class="bottom"><div id="status" class="status" role="status" aria-live="polite"></div><button id="share-site" class="share-site" hidden>Partager Nouveau chat ↗</button><div id="share-fallback" class="share-fallback" hidden><input id="share-url" type="url" readonly aria-label="Lien du site à copier"><button id="share-select">Sélectionner le lien</button></div><button id="bottom-new" class="again create"><span>Nouveau chat</span>$ARROW_ICON</button><p class="bottom-note">Des photos de chats. Pas d’intelligence artificielle.<br>Photos : <a href="https://cataas.com" target="_blank" rel="noopener noreferrer">Cataas</a> · Une expérience indépendante.</p></footer>
        </main>
    """.trimIndent()
    Sharing.initialize { showStatus(it) }
    element("sidebar-new").onclick = { newCat() }
    element("bottom-new").onclick = { newCat() }
    element("menu").onclick = { setSidebar(true) }
    element("close-menu").onclick = { setSidebar(false) }
    element("overlay").onclick = { setSidebar(false) }
    document.addEventListener("keydown", { event: dynamic ->
        if (event.key == "Escape" && sidebarOpen) setSidebar(false)
        if (event.key == "Tab" && sidebarOpen) {
            val focusable = element("sidebar").querySelectorAll("button:not(:disabled), a[href]")
            val first = focusable[0]
            val last = focusable[focusable.length - 1]
            if (event.shiftKey == true && document.activeElement == first) { event.preventDefault(); last.focus() }
            else if (event.shiftKey != true && document.activeElement == last) { event.preventDefault(); first.focus() }
        }
    })
    window.matchMedia("(max-width: 760px)").addEventListener("change", { _: dynamic -> setSidebar(false) })
    setSidebar(false, moveFocus = false)
    renderHistory()
    showStatus()
    if (!openSharedCat()) history.selected?.let { showCat(it) } ?: welcome()
}

/** Explicit shared link takes precedence over the browser's previous selection. */
private fun openSharedCat(): Boolean {
    val params = js("new URLSearchParams(window.location.search)")
    if (params.has("cat") != true) return false
    val values = params.getAll("cat")
    val id = params.get("cat") as? String
    if ((values.length as Int) != 1 || id == null || !CatPhoto.isValidId(id)) {
        showError("Ce lien de chat n’est pas valide. Vous pouvez rencontrer un nouveau chat.") { newCat() }
        return true
    }
    val photo = history.entries.firstOrNull { it.id == id }
        ?: CatPhoto(id, "Chat partagé", js("Date.now()") as Double)
    val token = ++viewToken
    setBusy(true)
    loading("Un chat vous a été partagé…")
    loadImage(photo.url).then({ _: Unit ->
        if (token == viewToken) {
            history.add(photo)
            persist()
            setBusy(false)
            renderPhoto(photo)
        }
        Unit
    }, { _: Throwable ->
        if (token == viewToken) {
            setBusy(false)
            Analytics.loadFailed("history_image")
            showError("Ce chat partagé est indisponible. Réessayez ou rencontrez un nouveau chat.") { openSharedCat() }
        }
        Unit
    })
    return true
}

private fun setSidebar(open: Boolean, moveFocus: Boolean = true) {
    sidebarOpen = open
    element("sidebar").classList.toggle("open", open)
    element("overlay").classList.toggle("open", open)
    element("menu").setAttribute("aria-expanded", open.toString())
    // Prevent keyboard focus from escaping the mobile drawer.
    document.querySelector("main").inert = open
    element("sidebar").inert = !open && window.matchMedia("(max-width: 760px)").matches == true
    document.body.style.overflow = if (open) "hidden" else ""
    if (moveFocus) {
        if (open) element("close-menu").focus() else element("menu").focus()
    }
}

private fun restore(): CatHistory {
    return try {
        val raw = window.localStorage.getItem(STORAGE_KEY) as String?
        val saved: dynamic = if (raw == null) js("[]") else JSON.parse<dynamic>(raw)
        if (!(js("Array.isArray(saved)") as Boolean)) error("Invalid history")
        val entries = mutableListOf<CatPhoto>()
        val count = (saved.length as Int).coerceAtMost(100)
        for (index in 0 until count) {
            val row = saved[index]
            val idValue: dynamic = row?.id
            val nameValue: dynamic = row?.name
            val timestampValue: dynamic = row?.createdAt
            val id = idValue as? String ?: continue
            val name = nameValue as? String ?: continue
            val timestamp = timestampValue as? Double ?: continue
            if (CatPhoto.isValidId(id) && timestamp.isFinite() && name.length in 1..100) entries.add(CatPhoto(id, name, timestamp))
        }
        CatHistory(entries).also { restored ->
            val selected = window.localStorage.getItem(SELECTED_KEY) as String?
            if (restored.entries.any { it.id == selected }) restored.selectedId = selected
        }
    } catch (_: Throwable) {
        persistenceWarning = "L’historique enregistré est illisible ou le stockage est bloqué. Les nouveaux chats restent disponibles pour cette session."
        CatHistory()
    }
}

private fun persist() {
    try {
        val rows = history.entries.map { photo ->
            val row = js("({})")
            row.id = photo.id; row.name = photo.name; row.createdAt = photo.createdAt
            row
        }.toTypedArray()
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rows))
        window.localStorage.setItem(SELECTED_KEY, history.selectedId ?: "")
        persistenceWarning = ""
    } catch (_: Throwable) {
        persistenceWarning = "Le navigateur ne permet pas d’enregistrer l’historique. Vos chats restent disponibles pour cette session."
    }
    showStatus()
}

private fun showStatus(message: String = "") {
    element("status").textContent = listOf(message, persistenceWarning).filter { it.isNotEmpty() }.joinToString(" ")
}

private fun renderHistory() {
    val list = element("history")
    list.replaceChildren()
    if (history.entries.isEmpty()) {
        val hint = document.createElement("p")
        hint.className = "history-empty"
        hint.textContent = "Aucun chat pour l’instant. Le premier n’attend que vous."
        list.appendChild(hint)
    }
    history.entries.forEach { photo ->
        val button = document.createElement("button")
        button.className = "history-item"
        button.setAttribute("aria-current", (photo.id == history.selectedId).toString())
        button.innerHTML = "$CAT_ICON<span></span>"
        button.querySelector("span").textContent = photo.name
        button.disabled = busy
        button.onclick = {
            history.selectedId = photo.id
            persist(); renderHistory(); setSidebar(false); showCat(photo)
        }
        list.appendChild(button)
    }
}

private fun setBusy(value: Boolean) {
    busy = value
    document.querySelectorAll(".create").forEach { button: dynamic -> button.disabled = value }
    element("stage").setAttribute("aria-busy", value.toString())
    renderHistory()
}

private fun welcome() {
    element("stage").innerHTML = """<div class="welcome"><div class="hero-icon">$CAT_ICON</div><h1>On ouvre un nouveau chat ?</h1><p class="subtitle">Un clic. Un chat.<br>Il ne répondra probablement pas.</p><button class="cta create" id="hero-new">$PLUS_ICON Nouveau chat</button><p class="micro">Aucune question n’est nécessaire.</p></div>"""
    element("hero-new").onclick = { newCat() }
}

private fun loading(text: String) {
    Sharing.selectPhoto(null)
    element("stage").innerHTML = """<div class="loading" role="status"><div class="loader" aria-hidden="true"></div><p></p></div>"""
    element("stage").querySelector("p").textContent = text
}

private fun showCat(photo: CatPhoto) {
    val token = ++viewToken
    loading("Votre chat arrive…")
    loadImage(photo.url).then({ _: Unit ->
        if (token == viewToken) renderPhoto(photo)
        Unit
    }, { _: Throwable ->
        if (token == viewToken) {
            Analytics.loadFailed("history_image")
            showError("Cette photo est momentanément indisponible. Votre historique est conservé.") { showCat(photo) }
        }
        Unit
    })
}

private fun renderPhoto(photo: CatPhoto, isNew: Boolean = false) {
    element("stage").innerHTML = """<article class="photo-view"><div class="photo-heading"><div class="avatar">$CAT_ICON</div><div><h1></h1><p>Un nouveau chat est entré dans la conversation.</p></div></div><img id="cat-image" class="cat-image" referrerpolicy="no-referrer"><div class="photo-caption"><span>Voilà. C’est un chat.</span><a id="original" target="_blank" rel="noopener noreferrer">Voir l’original ↗</a></div></article>"""
    element("stage").querySelector("h1").textContent = photo.name
    Sharing.selectPhoto(photo.id)
    // Replace, rather than push, to keep reloads aligned with the displayed photo.
    val pageUrl = js("new URL(window.location.href)")
    pageUrl.searchParams.set("cat", photo.id)
    try { window.history.replaceState(null, "", pageUrl.href) } catch (_: Throwable) { }
    val img = element("cat-image")
    img.alt = "Photo de félin : ${photo.name}"
    var counted = false
    img.onload = {
        if (isNew && !counted) {
            counted = true
            Analytics.catDisplayed()
            Sharing.photoDisplayed()
        }
    }
    img.onerror = {
        Analytics.loadFailed("display_image")
        showError("Cette photo ne se charge plus. Votre historique est conservé.") { showCat(photo) }
    }
    img.src = photo.url
    element("original").href = photo.url
}

private fun showError(message: String, retry: () -> Unit) {
    Sharing.selectPhoto(null)
    element("stage").innerHTML = """<div class="error-card" role="alert"><div class="hero-icon">$CAT_ICON</div><h1>Le chat se fait désirer.</h1><p></p><button class="cta" id="retry">Réessayer</button></div>"""
    element("stage").querySelector("p").textContent = message
    element("retry").onclick = { retry() }
}

private fun loadImage(url: String): Promise<Unit> = Promise { resolve, reject ->
    val img = js("new Image()")
    var finished = false
    var timer: dynamic = null
    fun finish(ok: Boolean) {
        if (!finished) {
            finished = true
            window.clearTimeout(timer)
            img.onload = null; img.onerror = null
            if (ok) resolve(Unit) else { img.src = ""; reject(Exception("Image unavailable")) }
        }
    }
    img.referrerPolicy = "no-referrer"
    img.onload = { finish((img.naturalWidth as Int) > 0) }
    img.onerror = { finish(false) }
    timer = window.setTimeout({ finish(false) }, 20000)
    img.src = url
}

private fun newCat() {
    if (busy) return
    ++viewToken // Ignore any older image load completing after this action.
    setSidebar(false)
    setBusy(true)
    showStatus()
    loading("À la recherche d’un nouveau chat…")
    val controller = js("new AbortController()")
    val timer = window.setTimeout({ controller.abort() }, 15000)
    val options = js("({})")
    options.signal = controller.signal
    options.cache = "no-store"
    options.credentials = "omit"
    var failureReason = "api"
    val request: dynamic = window.fetch("https://cataas.com/cat?json=true&nonce=" + (js("Date.now()") as Double), options)
    request.then { response: dynamic ->
        if (response.ok != true) throw Exception("HTTP error")
        response.json()
    }.then { data: dynamic ->
        window.clearTimeout(timer)
        val id = data.id as? String ?: throw Exception("Missing ID")
        if (!CatPhoto.isValidId(id)) throw Exception("Invalid ID")
        val names = listOf("Moustache", "Pixel", "Croquette", "Chaussette", "Ronron", "Biscotte", "Kotlin", "Câlin")
        val name = names[history.entries.size % names.size] + " · " + ((js("Date.now()") as Double).toLong() % 10000).toString().padStart(4, '0')
        val photo = CatPhoto(id, name, js("Date.now()") as Double)
        failureReason = "image"
        loadImage(photo.url).then {
            history.add(photo)
            persist()
            setBusy(false)
            renderPhoto(photo, isNew = true)
            showStatus("Un nouveau chat a rejoint votre historique.")
        }
    }.catch { _: dynamic ->
        window.clearTimeout(timer)
        setBusy(false)
        Analytics.loadFailed(failureReason)
        showError("Impossible de récupérer une photo. Vérifiez votre connexion puis réessayez. Vos anciens chats sont toujours là.") { newCat() }
    }
}
